import {ChangeDetectionStrategy, Component, OnInit, Optional} from '@angular/core';
import {LoaderService} from '../../../../../services/loader/loader.service';
import {ProductService} from '../../../../../services/product/product.service';
import {Product} from '../../../../../models/product';
import {NgOptimizedImage} from '@angular/common';
import {forkJoin} from 'rxjs';
import {TrackingDomainService} from '../../../../../services/tracking-domain/tracking-domain.service';
import {TrackingDomain} from '../../../../../models/tracking-domain';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {PartnerTabComponent} from '../../partner-tab/partner-tab.component';
import {Partner} from '../../../../../models/partner/partner';
import {LandingPageService} from '../../../../../services/landing-page/landing-page.service';
import {LandingPage} from '../../../../../models/landing-page/landing-page';
import {DialogService} from '../../../../../services/dialog/dialog.service';
import {TrackingDomainFormComponent} from '../tracking-domain-form/tracking-domain-form.component';
import {ClipboardService} from '../../../../../services/clipboard/clipboard.service';
import {Offer} from '../../../../../models/offer';
import {OfferService} from '../../../../../services/offer/offer.service';
import {LinkParameters} from '../../../../../models/adserver/link-parameters';
import {ImageService} from '../../../../../services/image/image.service';
import {MetaTitle} from '../../../../../decorator/meta-title';
import {ElementLoaderDirective} from '../../../../../directives/element-loader/element-loader.directive';
import {TrafficSource} from '../../../../../models/traffic-source';
import {TrafficSourceService} from '../../../../../services/traffic-source/traffic-source.service';
import {TranslatePipe} from '@ngx-translate/core';
import {AuthService} from '../../../../../services/auth/auth.service';
import {PartnerOfferService} from '../../../../../services/partner-offer/partner-offer.service';
import {PageTypeService} from '../../../../../services/page-type/page-type.service';
import {PageType} from '../../../../../models/page-type';
import {Pool} from '../../../../../models/landing-page/pool';
import {PoolService} from '../../../../../services/landing-page/pool.service';

@Component({
  selector: 'app-partner-link',
  imports: [
    NgOptimizedImage,
    ReactiveFormsModule,
    FormsModule,
    ElementLoaderDirective,
    TranslatePipe
  ],
  templateUrl: './partner-link.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './partner-link.component.scss'
})
@MetaTitle('Links')
export class PartnerLinkComponent implements OnInit {
  protected readonly Offer = Offer;

  loaded = false;
  partner: Partner | null = null;
  products: Product[] = [];
  product: Product | null = null;
  offers: Offer[] | null = null;
  pageTypes: PageType[] = [];
  pools: Pool[] = [];
  landingPages: LandingPage[] = [];
  trackingDomains: TrackingDomain[] = [];
  trackingDomain: TrackingDomain | null = null;
  pageType: PageType | null = null;
  pool: Pool | null = null;
  landingPage: LandingPage | null = null;
  conversionType: string | null = null;
  offer: Offer | null = null;
  copied = false;
  trackingDomainsLoading = false;
  trafficSources: TrafficSource[] = [];
  conversionTypeList: string[] = [];
  linkType = 'lp';

  options: LinkParameters = new LinkParameters();

  constructor(
    protected imageService: ImageService,
    private dialog: DialogService,
    private loader: LoaderService,
    private auth: AuthService,
    private clipboard: ClipboardService,
    private productService: ProductService,
    private trackingDomainService: TrackingDomainService,
    private landingPageService: LandingPageService,
    private offerService: OfferService,
    private poolService: PoolService,
    private pageTypeService: PageTypeService,
    private trafficSourceService: TrafficSourceService,
    private partnerOfferService: PartnerOfferService,
    @Optional() private partnerTab: PartnerTabComponent
  ) {
  }

  ngOnInit(): void {
    this.partnerTab.partnerSubject.subscribe(partner => {
      this.loader.startLoading();

      const trafficSourceParameters: any = {};

      this.partner = partner;
      this.options.affid = String(this.partner?.id ?? '');
      if (this.partner?.isMediaBuy() && this.partner?.advertisingPlatform?.id) {
        trafficSourceParameters.advertisingPlatforms = this.partner?.advertisingPlatform?.id;
      }

      forkJoin([
        this.productService.findAll({sort: 'name', enabled: true}),
        this.poolService.findAll({sort: 'name'}),
        this.pageTypeService.findAll({sort: 'name'}),
        this.trafficSourceService.findAll(trafficSourceParameters),
        this.auth.getAsyncConfig(),
      ]).subscribe(([products, pools, pageTypes, trafficSources, config]) => {
        this.products = products;
        this.pools = pools;
        this.pageTypes = pageTypes;
        this.trafficSources = trafficSources;
        this.conversionTypeList = config?.data?.conversionTypeList ?? [];

        this.product = this.products[0] ?? null;

        this.refreshTrackingDomain();
        this.refreshOffers();
        this.refreshLandingPages();
        this.refreshTracker();

        this.loader.stopLoading();
        this.loaded = true;
      });
    });
  }

  refreshTracker(): void {
    if (this.partner?.isGoogleAdvertiser()) {
      this.options.subid = this.partner?.advertisingPlatforms[0]?.authenticationInfo?.customerId || 'No Customer ID yet';

      if (this.options.source === 'pmax') {
        this.options.tc1 = '{campaignid}';
        this.options.tc2 = '{assetgroupid}';
        this.options.tc3 = '';
        this.options.tc4 = ''
      } else {
        this.options.tc1 = '{campaignid}';
        this.options.tc2 = '{adgroupid}';
        this.options.tc3 = '{creative}';
        this.options.tc4 = '{placement}'
      }
    } else if (this.partner?.isMetaAdvertiser()) {
      this.options.subid = this.partner?.advertisingPlatforms[0]?.authenticationInfo?.accountId || 'No Account ID yet';
      this.options.tc1 = '{{campaign.id}}';
      this.options.tc2 = '{{adset.id}}';
      this.options.tc3 = '{{ad.id}}';
      this.options.tc4 = '{{placement}}'
    } else if (this.partner?.isMicrosoftAdvertiser()) {
      this.options.subid = this.partner?.advertisingPlatforms[0]?.authenticationInfo?.accountId || 'No Account ID yet';
      this.options.tc1 = '{CampaignId}';
      this.options.tc2 = '{AdGroupId}';
      this.options.tc3 = '{AdId}';
      this.options.tc4 = '{Keyword}'
    } else if (this.partner?.isXAdvertiser()) {
      this.options.subid = this.partner?.advertisingPlatforms[0]?.authenticationInfo?.accountId || 'No Account ID yet';
      this.options.tc1 = '{{campaign_id}}';
      this.options.tc2 = '{{adgroup_id}}';
      this.options.tc3 = '{{ad_id}}';
      this.options.tc4 = '{{placement}}'
    } else if (this.partner?.isTiktokAdvertiser()) {
      this.options.subid = this.partner?.advertisingPlatforms[0]?.authenticationInfo?.accountId || 'No Account ID yet';
      this.options.tc1 = '__CAMPAIGN_ID__';
      this.options.tc2 = '__AID__';
      this.options.tc3 = '__CID__';
      this.options.tc4 = '__PLACEMENT__'
    }
  }

  isGoogleAdvertiser(): boolean {
    return !!this.partner?.isGoogleAdvertiser();
  }

  isMetaAdvertiser(): boolean {
    return !!this.partner?.isMetaAdvertiser();
  }

  isMicrosoftAdvertiser(): boolean {
    return !!this.partner?.isMicrosoftAdvertiser();
  }

  isXAdvertiser(): boolean {
    return !!this.partner?.isXAdvertiser();
  }

  isTiktokAdvertiser(): boolean {
    return !!this.partner?.isTiktokAdvertiser();
  }

  isAdvertiser(): boolean {
    return this.isGoogleAdvertiser() || this.isMetaAdvertiser() || this.isMicrosoftAdvertiser() || this.isXAdvertiser() || this.isTiktokAdvertiser();
  }

  getLink(): string {
    if (!this.trackingDomain?.domain) {
      return '';
    }

    let url = 'https://' + this.trackingDomain.domain + '/click?';
    let first = true;

    for (const [param, value] of Object.entries(this.options)) {
      if (typeof value !== 'string' || value.length > 0) {
        if (first) {
          first = false;
        } else {
          url += '&';
        }

        url += param + '=' + this.encodeParameter(value.toString().trim());
      }
    }

    return url;
  }

  getDirectLink(): string {
    if (!this.landingPage) {
      return '';
    }

    let url = this.landingPage.url ?? '';
    let first = true;

    for (const [param, value] of Object.entries(this.options)) {
      if (typeof value !== 'string' || value.length > 0) {
        if (first) {
          first = false;
          url += '?';
        } else {
          url += '&';
        }

        url += param + '=' + this.encodeParameter(value.toString().trim());
      }
    }

    return url;
  }

  encodeParameter(parameter: string): string {

    let encodedParameter = encodeURIComponent(parameter);

    let ignore = ['{', '}'];

    ignore.forEach(char => {
      encodedParameter = encodedParameter.replaceAll(encodeURIComponent(char), char);
    });

    return encodedParameter;
  }

  refreshTrackingDomain(selectedId: number | null = null): void {
    this.trackingDomainsLoading = true;

    this.trackingDomainService.findAll({
      partnerId: this.partner?.id,
      productId: this.product?.id,
      sort: 'domain'
    }).subscribe(trackingDomains => {
      this.trackingDomains = trackingDomains;
      this.trackingDomain = this.trackingDomains.find((trackingDomain: TrackingDomain) => trackingDomain.id === selectedId) ?? null;
      if (this.trackingDomain === null && this.trackingDomains.length) {
        this.trackingDomain = this.trackingDomains[0];
      }
      this.trackingDomainsLoading = false;
    });
  }

  createTrackingDomain(): void {
    this.dialog.openDialog(TrackingDomainFormComponent, {
      data: {
        products: this.products,
        partner: this.partner
      },
      backdrop: false
    }).subscribe((data) => {
      if (data) {
        this.refreshTrackingDomain(data.id);
      }
    });
  }

  modifyTrackingDomain(): void {
    if (this.trackingDomain) {
      this.dialog.openDialog(TrackingDomainFormComponent, {
        data: {
          products: this.products,
          partner: this.partner,
          trackingDomain: this.trackingDomain
        },
        backdrop: false
      }).subscribe(() => {
        this.refreshTrackingDomain(this.trackingDomain?.id);
      });
    }
  }

  copyUrl(url: string) {
    this.copied = true;
    this.clipboard.copy(url);
    setTimeout(() => {
      this.copied = false;
    }, 2000);
  }

  productChanged(): void {
    this.offer = null;

    this.refreshOffers();
    this.refreshTrackingDomain();
    this.refreshLandingPages();
  }

  pageTypeChanged(): void {
    this.refreshPools();
    this.refreshLandingPages();
  }

  linkTypeChanged(): void {
    if (this.linkType === 'lp') {
      this.options.pool = '';
      this.options.lp = this.landingPage?.id?.toString() ?? '';
    }

    if (this.linkType === 'pool') {
      this.options.lp = '';
      this.options.pool = this.pool?.identifier?.toString() ?? '';
    }
  }

  poolChanged(): void {
    if (this.pool?.identifier) {
      this.options.pool = this.pool.identifier;
    } else {
      this.options.pool = '';
    }
  }

  landingPageChanged(): void {
    if (this.landingPage?.id) {
      this.options.lp = this.landingPage.id.toString();
    } else {
      this.options.lp = '';
    }
  }

  conversionTypeChanged(): void {
    this.refreshOffers();
  }

  offerChanged(): void {
    this.refreshLandingPages();
  }

  refreshOffers(): void {
    this.offers = [];

    if (this.product) {
      const parameters: any = {
        productId: this.product?.id
      };

      if (this.partner) {
        if (this.partner.isAffiliate() && this.conversionType) {
          parameters.conversionType = this.conversionType;
        }

        this.offerService.findPartnerOffers(this.partner, parameters).subscribe((offers: Offer[]) => {
          this.offers = offers;

          if (this.offers?.findIndex(offer => offer.id == this.options.oid) === -1) {
            this.options.oid = '';
          }
        });
      }
    }
  }

  getOfferDefaultText(): string {
    if (this.offers?.length) {
      return 'Select offer (' + this.offers?.length + ' found)';
    } else {
      return 'No offer found'
    }
  }

  isOfferDisabled(): boolean {
    return !this.offers?.length;
  }

  refreshPools(): void {
    this.pools = [];
    this.pool = null;
    this.options.pool = '';

    const params: any = {
      sort: 'name',
      type: this.pageType,
      enabled: true,
    };

    this.poolService.findAll(params).subscribe((pools: Pool[]) => {
      this.pools = pools;
    });
  }

  refreshLandingPages(): void {
    this.landingPages = [];
    this.landingPage = null;
    this.options.lp = '';
    this.options.oid = '';

    if (this.offer) {
      this.options.oid = this.offer.id ?? '';

      const params: any = {
        sort: 'name',
        type: this.pageType,
        enabled: true,
        offerId: this.offer.id,
        groups: LandingPage.DETAILS_GROUP,
      };

      this.landingPageService.findAll(params).subscribe((landingPages: LandingPage[]) => {
        this.landingPages = landingPages;
      });
    }
  }

  getPoolDefaultText(): string {
    if (this.pools?.length) {
      return 'Select pool (' + this.pools?.length + ' found)';
    } else {
      return 'No pool found'
    }
  }

  getLandingPageDefaultText(): string {
    if (this.landingPages?.length) {
      return 'Select promotion link (' + this.landingPages?.length + ' found)';
    } else {
      return 'No promotion link found'
    }
  }

  isPoolDisabled(): boolean {
    return !this.pools?.length;
  }

  isLandingPageDisabled(): boolean {
    return !this.landingPages?.length;
  }

  isPromotionLinkValid(): boolean {
    return !!this.options.lp || !!this.options.pool;
  }

  isPromotionDirectLinkValid(): boolean {
    return !!this.landingPage && !this.landingPage.external;
  }
}
